import { useBxpWebStargateClient } from "../../../../libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client";
import { JiraFFLContentGenerationResponse } from "../../../../libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types";
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import { type MessageDescriptor } from "@formatjs/intl/src/types";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import {
    USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
    USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT,
    USER_SEGMENTATION_TEAM_TYPE_MARKETING,
    USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT,
    USER_SEGMENTATION_TEAM_TYPE_DESIGN,
    USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT
} from "@atlassian/onboarding-survey";

import { getRecaptcha } from "@/components/signup/utils/recaptcha";

import { getSuggestionsForTeamType } from "../../sections/cfind-4666-frictionless-first-look/messages";
import { type TableRowProps } from "../../sections/cfind-4666-frictionless-first-look/paid-jira-ai-preview-modal/preview-table/table-row";
import {
    categorizeTeamType,
    derivePromptType,
    firePaidJiraAiEvent,
    getCgsKeyFromLocationHref,
    PAID_JIRA_AI_EXPERIMENT_KEY,
    PAID_JIRA_AI_FLOW,
    safeErrorContext,
    type PaidJiraAiChangedField,
    type PaidJiraAiCommonAttributes,
    type PaidJiraAiPromptType,
    type PaidJiraAiStep,
    type PaidJiraAiTeamTypeCategory
} from "../../utils/paid-jira-ai-analytics";
import {
    ANALYTIC_EVENT_ACTION_FAILED,
    ANALYTIC_EVENT_ACTION_STARTED,
    ANALYTIC_EVENT_ACTION_SUCCEEDED,
    MADLIB_CONTENT_GENERATION_ANALYTIC_EVENT_NAME,
    MADLIB_FLOW,
    fallbackCgsData
} from "../../utils/paid-jira-ai-ffl";
import {
    formatCgsData,
    getAnchorConfig,
    getTeamTypeFromAnchor,
    teamTypesMap,
    type TeamType
} from "../../utils/paid-jira-ai-ffl";

export type SubmitParams = {
    teamType: TeamType;
    workingOn: string;
    goal: string;
};

export type JiraAiFFLContextType = {
    submit: (update: SubmitParams) => void;

    // Shared Madlib UI state
    selectedTeamType: TeamType;
    setSelectedTeamType: (teamType: TeamType) => void;

    workingOnSuggestion: string;
    goalSuggestion: string;

    workingOnValue: string;
    goalValue: string;
    setWorkingOnValue: (v: string) => void;
    setGoalValue: (v: string) => void;

    hasUserSelectedTeamType: boolean;
    setHasUserSelectedTeamType: (v: boolean) => void;

    hasSubmittedDefaultValues: boolean;

    shuffle: () => void;
    suggestMadLib: (teamType?: TeamType) => void;

    // Analytics (derived-only, no prompt strings)
    promptAttemptIndex: number;
    commonAnalyticsAttributes: PaidJiraAiCommonAttributes;
    firePaidJiraAi: (
        step: PaidJiraAiStep,
        action:
            | "viewed"
            | "started"
            | "clicked"
            | "succeeded"
            | "failed"
            | "closed",
        attributes?: Record<string, unknown>
    ) => void;
    markPromptChangedField: (field: PaidJiraAiChangedField) => void;

    // CGS response state
    cgsDataLoading: boolean;
    isErrorFromCgs: boolean;
    setIsErrorFromCgs: (v: boolean) => void;
    rawCgsData?: JiraFFLContentGenerationResponse;
    formattedCgsData?: Omit<TableRowProps, "isLastRow">[];
};

const noop = () => {
    /* empty */
};

const defaultContext: JiraAiFFLContextType = {
    submit: noop,

    selectedTeamType:
        USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT as TeamType,
    setSelectedTeamType: noop,

    workingOnSuggestion: "",
    goalSuggestion: "",

    workingOnValue: "",
    goalValue: "",
    setWorkingOnValue: noop,
    setGoalValue: noop,

    hasUserSelectedTeamType: false,
    setHasUserSelectedTeamType: noop,

    hasSubmittedDefaultValues: false,

    shuffle: noop,
    suggestMadLib: noop,

    promptAttemptIndex: 0,
    commonAnalyticsAttributes: {
        flow: PAID_JIRA_AI_FLOW,
        experimentKey: PAID_JIRA_AI_EXPERIMENT_KEY
    },
    firePaidJiraAi: noop,
    markPromptChangedField: noop,

    cgsDataLoading: false,
    isErrorFromCgs: false,
    rawCgsData: undefined,
    formattedCgsData: undefined,
    setIsErrorFromCgs: noop
};

const JiraAiFFLContext = createContext<JiraAiFFLContextType>(defaultContext);

export const JiraAiFFLProvider = ({
    children,
    anchor,
    subAnchor
}: {
    children: React.ReactNode;
    anchor?: string;
    subAnchor?: string;
}) => {
    const stargateClient = useBxpWebStargateClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const intl = useIntl();
    const { executeRecaptcha } = useGoogleReCaptcha();

    // Analytics state
    const [promptAttemptIndex, setPromptAttemptIndex] = useState(0);
    const promptAttemptIndexRef = useRef(0);
    const changedFieldsRef = useRef<Set<PaidJiraAiChangedField>>(new Set());
    const [changedFieldsVersion, setChangedFieldsVersion] = useState(0);
    const engagedAtRef = useRef<number | null>(null);
    const hasSubmittedThisAttemptRef = useRef(false);
    const hasFiredViewedRef = useRef(false);
    const hasFiredEngagedRef = useRef(false);
    const hasFiredAbandonedRef = useRef(false);

    // Shared Madlib UI state
    const [selectedTeamType, setSelectedTeamType] = useState<TeamType>(
        getTeamTypeFromAnchor(anchor, subAnchor) ??
            defaultContext.selectedTeamType
    );
    const [workingOnSuggestion, setWorkingOnSuggestion] = useState<string>("");
    const [goalSuggestion, setGoalSuggestion] = useState<string>("");
    const [workingOnValue, setWorkingOnValue] = useState<string>("");
    const [goalValue, setGoalValue] = useState<string>("");
    const [hasUserSelectedTeamType, setHasUserSelectedTeamType] =
        useState(false);
    const [hasSubmittedDefaultValues, setHasSubmittedDefaultValues] =
        useState(false);
    const initialSuggestion = useRef<{ workingOn: string; goal: string }>();

    const suggestionsIndex = useRef<Record<TeamType, number>>({
        [USER_SEGMENTATION_TEAM_TYPE_SOFTWARE]: 0,
        [USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT]: 0,
        [USER_SEGMENTATION_TEAM_TYPE_MARKETING]: 0,
        [USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT]: 0,
        [USER_SEGMENTATION_TEAM_TYPE_DESIGN]: 0,
        [USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT]: 0
    });

    // CGS state
    const [cgsDataLoading, setCgsDataLoading] = useState(false);
    const [isErrorFromCgs, setIsErrorFromCgs] = useState(false);
    const [cgsData, setCgsData] = useState<
        JiraFFLContentGenerationResponse | undefined
    >(undefined);

    useEffect(() => {
        promptAttemptIndexRef.current = promptAttemptIndex;
    }, [promptAttemptIndex]);

    const getChangedFields = () => Array.from(changedFieldsRef.current);

    const workingOnLength = useMemo(
        () => workingOnValue.trim().length,
        [workingOnValue]
    );
    const goalLength = useMemo(() => goalValue.trim().length, [goalValue]);

    const promptType: PaidJiraAiPromptType = useMemo(
        () =>
            derivePromptType({
                workingOnValue,
                goalValue,
                workingOnSuggestion,
                goalSuggestion
            }),
        [workingOnValue, goalValue, workingOnSuggestion, goalSuggestion]
    );

    const teamTypeCategory: PaidJiraAiTeamTypeCategory = useMemo(
        () => categorizeTeamType(selectedTeamType),
        [selectedTeamType]
    );

    const commonAnalyticsAttributes: PaidJiraAiCommonAttributes =
        useMemo(() => {
            const changedFields = getChangedFields();
            const cgsKey =
                typeof window !== "undefined"
                    ? getCgsKeyFromLocationHref(window.location.href)
                    : undefined;
            return {
                flow: PAID_JIRA_AI_FLOW,
                experimentKey: PAID_JIRA_AI_EXPERIMENT_KEY,
                teamType: selectedTeamType,
                teamTypeCategory,
                promptAttemptIndex,
                isRetry: promptAttemptIndex > 1,
                promptType,
                didChangeAnything: changedFields.length > 0,
                changedFields,
                workingOnLength,
                goalLength,
                cgsKey
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [
            selectedTeamType,
            teamTypeCategory,
            promptAttemptIndex,
            promptType,
            workingOnLength,
            goalLength,
            changedFieldsVersion
        ]);

    const firePaidJiraAi = useCallback(
        (
            step: PaidJiraAiStep,
            action:
                | "viewed"
                | "started"
                | "clicked"
                | "succeeded"
                | "failed"
                | "closed",
            attributes: Record<string, unknown> = {}
        ) => {
            firePaidJiraAiEvent(createAnalyticsEvent, {
                step,
                action,
                attributes: {
                    ...commonAnalyticsAttributes,
                    ...attributes
                }
            });
        },
        [createAnalyticsEvent, commonAnalyticsAttributes]
    );

    const markPromptEngaged = useCallback(() => {
        if (!engagedAtRef.current) {
            engagedAtRef.current = performance.now();
        }
        if (!hasFiredEngagedRef.current) {
            hasFiredEngagedRef.current = true;
            firePaidJiraAi("PromptEngaged", "started");
        }
    }, [firePaidJiraAi]);

    const markPromptChangedField = useCallback(
        (field: PaidJiraAiChangedField) => {
            markPromptEngaged();
            if (!changedFieldsRef.current.has(field)) {
                changedFieldsRef.current.add(field);
                setChangedFieldsVersion((v) => v + 1);
            }
        },
        [markPromptEngaged]
    );

    // Prompt view + abandonment tracking
    useEffect(() => {
        if (!hasFiredViewedRef.current) {
            hasFiredViewedRef.current = true;
            firePaidJiraAi("PromptViewed", "viewed");
        }

        const maybeFireAbandoned = () => {
            if (hasFiredAbandonedRef.current) {
                return;
            }
            if (!engagedAtRef.current || hasSubmittedThisAttemptRef.current) {
                return;
            }

            hasFiredAbandonedRef.current = true;
            firePaidJiraAi("PromptAbandoned", "closed", {
                dwellMs: Math.max(0, performance.now() - engagedAtRef.current)
            });
        };

        window.addEventListener("pagehide", maybeFireAbandoned);
        const onVisibilityChange = () => {
            if (document.visibilityState === "hidden") {
                maybeFireAbandoned();
            }
        };
        document.addEventListener("visibilitychange", onVisibilityChange);

        return () => {
            window.removeEventListener("pagehide", maybeFireAbandoned);
            document.removeEventListener(
                "visibilitychange",
                onVisibilityChange
            );
        };
    }, [firePaidJiraAi]);

    const suggestMadLib = (teamType?: TeamType) => {
        const t = teamType || selectedTeamType;
        const suggestions = getSuggestionsForTeamType(t);

        suggestionsIndex.current[t] = (suggestionsIndex.current[t] || 0) + 1;
        if (!suggestions[suggestionsIndex.current[t]]) {
            suggestionsIndex.current[t] = 0;
        }

        const suggestion = suggestions[suggestionsIndex.current[t]];
        const workingOnSuggestion = intl.formatMessage(
            suggestion?.task as MessageDescriptor
        );
        const goalSuggestion = intl.formatMessage(
            suggestion?.goal as MessageDescriptor
        );

        setWorkingOnSuggestion(workingOnSuggestion);
        setGoalSuggestion(goalSuggestion);

        if (!initialSuggestion.current) {
            initialSuggestion.current = {
                workingOn: workingOnSuggestion,
                goal: goalSuggestion
            };
        }

        setWorkingOnValue("");
        setGoalValue("");
    };
    // Compute anchor config synchronously during render to avoid a production
    // scope-hoisting issue where the minified `anchor` variable inside the
    // useEffect closure resolves to a different binding (markPromptChangedField).
    const anchorConfig = useMemo(
        () => getAnchorConfig(anchor, subAnchor),
        [anchor, subAnchor]
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (anchorConfig) {
            setSelectedTeamType(anchorConfig.teamType);
            suggestionsIndex.current[anchorConfig.teamType] =
                anchorConfig.suggestionIndex - 1;
            suggestMadLib(anchorConfig.teamType);
        } else {
            suggestMadLib();
        }
    }, [anchorConfig]);

    const shuffle = () => {
        markPromptChangedField("shuffle");
        firePaidJiraAi("PromptShuffleClicked", "clicked", {
            cta: "shuffle"
        });
        if (!hasUserSelectedTeamType) {
            const teamTypes = Object.keys(teamTypesMap) as TeamType[];
            const randomTeamType =
                teamTypes[Math.floor(Math.random() * teamTypes.length)];
            setSelectedTeamType(randomTeamType as TeamType);
            suggestMadLib(randomTeamType as TeamType);
        } else {
            suggestMadLib();
        }
    };

    const formattedCgsData = useMemo(() => {
        if (cgsDataLoading || !cgsData) {
            return undefined;
        }

        return formatCgsData(cgsData);
    }, [cgsData, cgsDataLoading]);

    const getRecaptchaOutcome = async () => {
        const recaptchaStart = performance.now();
        let token: string | undefined;
        let recaptchaError: unknown;

        try {
            token = await getRecaptcha(
                "", // paid landing is anonymous; token still binds to browser context
                executeRecaptcha
            );
        } catch (e) {
            recaptchaError = e;
            token = undefined;
        }

        const recaptchaTimeMs = Math.max(0, performance.now() - recaptchaStart);
        const result: "token" | "empty" | "error" = token
            ? "token"
            : recaptchaError
            ? "error"
            : "empty";

        if (token) {
            firePaidJiraAi("RecaptchaTokenSucceeded", "succeeded", {
                recaptchaTimeMs
            });
        } else {
            firePaidJiraAi("RecaptchaTokenFailed", "failed", {
                recaptchaTimeMs,
                failureReason: recaptchaError ? "error" : "empty",
                ...safeErrorContext(recaptchaError)
            });
        }

        return {
            token: token ?? "",
            recaptchaTimeMs,
            result
        };
    };

    const setCgsKeyInUrl = (cgsKey: string) => {
        // Persist cgsKey in the URL so downstream flows (e.g. account creation)
        // can pick it up without depending on this context provider.
        const nextUrl = new URL(window.location.href);
        nextUrl.searchParams.set("cgsKey", cgsKey);
        window.history.replaceState(null, "", nextUrl.toString());
    };

    const submit = async (update: SubmitParams) => {
        const { teamType, workingOn, goal } = update;
        const attemptIndex = promptAttemptIndexRef.current + 1;
        const submitStart = performance.now();
        hasSubmittedThisAttemptRef.current = true;
        hasFiredAbandonedRef.current = false;

        const derived = {
            promptAttemptIndex: attemptIndex,
            isRetry: attemptIndex > 1,
            promptType,
            didChangeAnything: getChangedFields().length > 0,
            changedFields: getChangedFields(),
            workingOnLength,
            goalLength
        };

        setPromptAttemptIndex(attemptIndex);
        setCgsDataLoading(true);
        setIsErrorFromCgs(false);

        let recaptchaOutcome:
            | {
                  token: string;
                  recaptchaTimeMs: number;
                  result: "token" | "empty" | "error";
              }
            | undefined;

        try {
            // Explicit lifecycle start (click is tracked separately in the prompt component).
            createAnalyticsEvent({
                action: ANALYTIC_EVENT_ACTION_STARTED,
                eventType: "track",
                actionSubject: MADLIB_CONTENT_GENERATION_ANALYTIC_EVENT_NAME,
                attributes: {
                    teamType,
                    flow: MADLIB_FLOW,
                    ...derived
                }
            }).fire();

            firePaidJiraAi("ContentGenStarted", "started", derived);

            // Be resilient: even if reCAPTCHA is empty/errored, proceed and let the API
            // decide whether it's required.
            recaptchaOutcome = await getRecaptchaOutcome();

            setHasSubmittedDefaultValues(
                teamType === defaultContext.selectedTeamType &&
                    workingOn === initialSuggestion.current?.workingOn &&
                    goal === initialSuggestion.current?.goal
            );

            const response =
                await stargateClient.postContentGenerationForJiraFfl({
                    teamType,
                    workingOnSummary: workingOn,
                    goal,
                    gRecaptchaResponse: recaptchaOutcome.token
                });

            setCgsData(response);

            setCgsKeyInUrl(response.cacheKey);

            setCgsDataLoading(false);
            const rowCount = formatCgsData(response)?.length ?? 0;
            createAnalyticsEvent({
                action: ANALYTIC_EVENT_ACTION_SUCCEEDED,
                eventType: "track",
                actionSubject: MADLIB_CONTENT_GENERATION_ANALYTIC_EVENT_NAME,
                attributes: {
                    teamType,
                    flow: MADLIB_FLOW,
                    timeToCgsMs: Math.max(0, performance.now() - submitStart),
                    rowCount,
                    recaptchaTimeMs: recaptchaOutcome.recaptchaTimeMs,
                    recaptchaResult: recaptchaOutcome.result,
                    ...derived
                }
            }).fire();

            firePaidJiraAi("ContentGenSucceeded", "succeeded", {
                timeToCgsMs: Math.max(0, performance.now() - submitStart),
                rowCount,
                recaptchaTimeMs: recaptchaOutcome.recaptchaTimeMs,
                recaptchaResult: recaptchaOutcome.result,
                ...derived
            });
        } catch (error) {
            setCgsDataLoading(false);
            setIsErrorFromCgs(true);
            setCgsData(fallbackCgsData);

            setCgsKeyInUrl(fallbackCgsData.cacheKey);

            const safe = safeErrorContext(error);
            createAnalyticsEvent({
                action: ANALYTIC_EVENT_ACTION_FAILED,
                eventType: "track",
                actionSubject: MADLIB_CONTENT_GENERATION_ANALYTIC_EVENT_NAME,
                attributes: {
                    teamType,
                    flow: MADLIB_FLOW,
                    error: (error as Error).message,
                    timeToCgsMs: Math.max(0, performance.now() - submitStart),
                    recaptchaTimeMs: recaptchaOutcome?.recaptchaTimeMs,
                    recaptchaResult: recaptchaOutcome?.result,
                    ...derived,
                    ...safe
                }
            }).fire();

            firePaidJiraAi("ContentGenFailed", "failed", {
                timeToCgsMs: Math.max(0, performance.now() - submitStart),
                recaptchaTimeMs: recaptchaOutcome?.recaptchaTimeMs,
                recaptchaResult: recaptchaOutcome?.result,
                ...derived,
                ...safe
            });
        } finally {
            // Reset engagement tracking for the next attempt.
            engagedAtRef.current = null;
            hasSubmittedThisAttemptRef.current = false;
            hasFiredEngagedRef.current = false;
            changedFieldsRef.current = new Set();
            setChangedFieldsVersion((v) => v + 1);
        }
    };

    const value = useMemo(
        () => ({
            submit,

            // Madlib UI state
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
            selectedTeamType: selectedTeamType as TeamType,
            setSelectedTeamType,
            workingOnSuggestion,
            goalSuggestion,
            workingOnValue,
            goalValue,
            setWorkingOnValue,
            setGoalValue,
            hasUserSelectedTeamType,
            setHasUserSelectedTeamType,
            hasSubmittedDefaultValues,
            shuffle,
            suggestMadLib,
            promptAttemptIndex,
            commonAnalyticsAttributes,
            firePaidJiraAi,
            markPromptChangedField,

            // CGS
            cgsDataLoading,
            isErrorFromCgs,
            rawCgsData: cgsData,
            formattedCgsData,
            setIsErrorFromCgs
        }),
        [
            submit,
            selectedTeamType,
            workingOnSuggestion,
            goalSuggestion,
            workingOnValue,
            goalValue,
            hasUserSelectedTeamType,
            hasSubmittedDefaultValues,
            shuffle,
            suggestMadLib,
            promptAttemptIndex,
            commonAnalyticsAttributes,
            firePaidJiraAi,
            markPromptChangedField,
            cgsDataLoading,
            isErrorFromCgs,
            cgsData,
            formattedCgsData,
            setIsErrorFromCgs
        ]
    );

    return (
        <JiraAiFFLContext.Provider value={value}>
            {children}
        </JiraAiFFLContext.Provider>
    );
};

export const useJiraAiFFL = () => useContext(JiraAiFFLContext);
